import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18n } from "../../hooks/useI18n";
import { AppState } from "../../store";
import { Market } from "../../models/Market";
import { thunkFetchMarkets } from "../../store/markets/actions";
import { asList } from "../../hooks/useLoadIfEmpty";
import { OnBoardingEditor } from "./OnBoardingEditor";
import { SupportEditor } from "./SupportEditor";
import { LanguageMultiSelect } from "../../components/selects/LanguageMultiSelect";
import { LanguageSelect } from "../../components/selects/LanguageSelect";
import MaterialTable from "@material-table/core";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { Alert } from "@material-ui/lab";
import { getFlags } from "../../utils";
import { VerificationMethodMultiSelect } from "../../components/selects/VerificationMethodMultiSelect";
import * as marketApi from "../../api/marketApi";
import useStyles from "./styles";

export const MarketList = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useI18n();

  const markets = asList<Market>(
    useSelector<AppState, unknown>(s => s.markets)
  );

  const [state, setState] = useState<any>({
    reloadList: false,
    editingMarket: {} as Market,
    showEditMarketDialog: false,
    showEditOnBoardingsDialog: false,
    showEditSupportDialog: false,
    addOrUpdateMarketResult: {}
  });

  useEffect(() => {
    if (!markets || !markets.length || state.reloadList) {
      dispatch(thunkFetchMarkets());
    }
  }, [state]);

  const mapToPriceUnits = (commaSepUnits: string) => {
    const list = commaSepUnits.split(",").map(x => {
      return { unit: x.trim() } as PriceUnit;
    });

    setState({
      ...state,
      editingMarket: {
        ...state.editingMarket,
        priceUnits: list
      }
    });
  };

  const mapToVat = (commaSepUnits: string) => {
    const list = commaSepUnits.split(",").map(x => {
      const p = parseInt(x);
      return { percentage: isNaN(p) ? x : p } as Vat;
    });

    setState({
      ...state,
      editingMarket: {
        ...state.editingMarket,
        vats: list
      }
    });
  };

  const mapToDepositFee = (commaSepUnits: string) => {
    const list = commaSepUnits.split(",").map(x => {
      const p = x.trim();
      return { amount: p.length > 2 ? parseFloat(p) : p } as DepositFee;
    });

    setState({
      ...state,
      editingMarket: {
        ...state.editingMarket,
        depositFees: list
      }
    });
  };

  const disabled = () => {
    return (
      !state.editingMarket?.name ||
      !state.editingMarket?.countryCode ||
      !state.editingMarket?.currency ||
      !state.editingMarket?.currencySymbol ||
      !state.editingMarket?.vats ||
      !state.editingMarket?.priceUnits ||
      !state.editingMarket?.loginMethods
    );
  };

  return (
    <>
      <MaterialTable
        title=""
        columns={[
          {
            title: "",
            field: "flag",
            width: "20px",
            render: row => (
              <Typography variant={"h4"}>
                {getFlags(row.countryCode)}
              </Typography>
            )
          },
          { title: t("common.formFields.name"), field: "name" },
          { title: t("common.formFields.countryCode"), field: "countryCode" },
          { title: t("common.formFields.currency"), field: "currency" },
          {
            title: t("common.formFields.currencySymbol"),
            field: "currencySymbol"
          }
        ]}
        data={
          markets && markets.length > 0
            ? markets.map(m => ({
                id: m.id,
                name: m.name,
                countryCode: m.countryCode,
                currency: m.currency,
                currencySymbol: m.currencySymbol
              }))
            : []
        }
        options={{
          actionsColumnIndex: -1,
          actionsCellStyle: { whiteSpace: "nowrap" },
          pageSize: 10
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
        actions={[
          {
            icon: "add_circle",
            iconProps: {
              style: { color: "rgb(31, 132, 52)", fontSize: "2rem" }
            },
            tooltip: t("common.actions.addMarket"),
            isFreeAction: true,
            onClick: () => {
              setState({
                ...state,
                showEditMarketDialog: true,
                editingMarket: { hasDecimalCurrency: true } as Market
              });
            }
          },
          {
            icon: "edit",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editMarket"),
            onClick: (event, rowData: any) => {
              const editingMarket = markets.find(m => m.id == rowData?.id);
              setState({
                ...state,
                showEditMarketDialog: true,
                editingMarket: editingMarket
              });
            }
          },
          {
            icon: "post_add",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editContent"),
            onClick: (event, rowData: any) => {
              const editingMarket = markets.find(m => m.id == rowData?.id);
              setState({
                ...state,
                showEditOnBoardingsDialog: true,
                editingMarket: editingMarket
              });
            }
          },
          {
            icon: "support_agent",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editSupport"),
            onClick: (event, rowData: any) => {
              const editingMarket = markets.find(m => m.id == rowData?.id);
              setState({
                ...state,
                showEditSupportDialog: true,
                editingMarket: editingMarket
              });
            }
          }
        ]}
      />
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showEditMarketDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => setState({ ...state, showEditMarketDialog: false })}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginBottom: 10 }}>
            <Grid item xs={12}>
              <TextField
                className={classes.field}
                label={t("common.formFields.name")}
                required
                value={state.editingMarket?.name}
                onChange={e =>
                  setState({
                    ...state,
                    editingMarket: {
                      ...state.editingMarket,
                      name: e.target.value
                    }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                className={classes.field}
                label={t("common.formFields.countryCode")}
                required
                inputProps={{
                  style: { textTransform: "uppercase" },
                  maxLength: 2
                }}
                value={
                  state.editingMarket?.countryCode
                    ? state.editingMarket?.countryCode.toUpperCase()
                    : ""
                }
                onChange={e =>
                  setState({
                    ...state,
                    editingMarket: {
                      ...state.editingMarket,
                      countryCode: e.target.value
                    }
                  })
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant={"h6"}>
                        {getFlags(state.editingMarket.countryCode)}
                      </Typography>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                className={classes.field}
                label={t("common.formFields.currency")}
                inputProps={{
                  style: { textTransform: "uppercase" },
                  maxLength: 3
                }}
                required
                value={state.editingMarket?.currency}
                onChange={e =>
                  setState({
                    ...state,
                    editingMarket: {
                      ...state.editingMarket,
                      currency: e.target.value
                    }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                className={classes.field}
                label={t("common.formFields.currencySymbol")}
                required
                value={state.editingMarket?.currencySymbol}
                onChange={e =>
                  setState({
                    ...state,
                    editingMarket: {
                      ...state.editingMarket,
                      currencySymbol: e.target.value
                    }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LanguageSelect
                label={t("common.formFields.currencyFormat")}
                className={classes.field}
                value={state.editingMarket?.currencyLanguageId}
                onSelected={language =>
                  setState({
                    ...state,
                    editingMarket: {
                      ...state.editingMarket,
                      currencyLanguageId: language.id
                    }
                  })
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              style={{ display: "flex", flexDirection: "row" }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    value={state.editingMarket?.hasDecimalCurrency}
                    checked={state.editingMarket?.hasDecimalCurrency}
                    color={"primary"}
                    onChange={(_, checked) => {
                      setState({
                        ...state,
                        editingMarket: {
                          ...state.editingMarket,
                          hasDecimalCurrency: checked
                        }
                      });
                    }}
                  />
                }
                label={t("common.formFields.decimalCurrency", {
                  decimalCurrency: state.editingMarket?.hasDecimalCurrency
                    ? "(0,00kr)"
                    : "(0kr)"
                })}
                labelPlacement={"end"}
              />
            </Grid>
          </Grid>
          <LanguageMultiSelect
            label={t("common.formFields.language")}
            className={classes.field}
            value={state.editingMarket?.languageIds}
            onSelected={languages =>
              setState({
                ...state,
                editingMarket: {
                  ...state.editingMarket,
                  languageIds: languages.map((x: any) => x.id)
                }
              })
            }
          />
          <TextField
            className={classes.field}
            label={t("common.formFields.vat")}
            required
            value={state.editingMarket?.vats?.map((x: any) => x.percentage)}
            onChange={e => mapToVat(e.target.value)}
          />
          <TextField
            className={classes.field}
            required
            label={t("common.formFields.priceUnits")}
            value={state.editingMarket?.priceUnits?.map((x: any) => x.unit)}
            onChange={e => mapToPriceUnits(e.target.value)}
          />
          <TextField
            className={classes.field}
            label={`${t("common.formFields.depositFees")} in ${
              state.editingMarket.currency
            } (comma separated)`}
            value={state.editingMarket?.depositFees?.map((x: any) => x.amount)}
            onChange={e => mapToDepositFee(e.target.value)}
          />
          <VerificationMethodMultiSelect
            label={t("common.formFields.loginMethods")}
            className={classes.field}
            required
            value={state.editingMarket?.loginMethods?.map((x: any) => x.id)}
            onSelected={loginMethods =>
              setState({
                ...state,
                editingMarket: {
                  ...state.editingMarket,
                  loginMethods: loginMethods
                }
              })
            }
          />
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 30 }}
          >
            <Button
              style={{ width: 200 }}
              variant="contained"
              color="primary"
              disabled={disabled()}
              onClick={async () => {
                const result = await marketApi.upsert(state.editingMarket);
                if (result.type !== "OK") {
                  setState({ ...state, editOrUpdateMarketFailed: true });
                } else {
                  setState({
                    ...state,
                    showEditMarketDialog: false,
                    reloadList: true,
                    upsertSuccess: true
                  });
                }
              }}
            >
              {t("common.actions.saveMarket")}
            </Button>
            {state.editOrUpdateMarketFailed && (
              <Alert severity="error">
                {t("common.errors.addOrUpdateMarketFailed")}
              </Alert>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={state.showEditOnBoardingsDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() =>
              setState({ ...state, showEditOnBoardingsDialog: false })
            }
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <OnBoardingEditor
            marketId={state.editingMarket.id}
            markets={markets}
            onSave={() => {
              setState({ ...state, reloadList: true });
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showEditSupportDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => setState({ ...state, showEditSupportDialog: false })}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <SupportEditor
            marketId={state.editingMarket.id}
            support={state.editingMarket.content?.support}
            onSave={() => {
              setState({
                ...state,
                reloadList: true,
                upsertSuccess: true,
                showEditSupportDialog: false
              });
            }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={state.upsertSuccess}
        autoHideDuration={3000}
        onClose={() => {
          setState({
            ...state,
            upsertSuccess: false
          });
        }}
      >
        <Alert severity={"success"}>
          {t("common.success.addOrUpdateMarket")}
        </Alert>
      </Snackbar>
    </>
  );
};
